import React from 'react';
import Link from 'next/link';
import {
  Activity,
  ArrowRight,
  CirclePlay,
  CircleQuestionMark,
  ExternalLink,
  Mail,
  Truck,
} from 'lucide-react';

import { Head, Layout } from '../components/layout';
import CopyEmailButton, { SUPPORT_EMAIL } from '../components/support/copyEmailButton';
import styles from '../components/support/support.module.scss';
import SETTINGS from '../settings';

const HELP_PATHS = [
  {
    id: 'faq',
    Icon: CircleQuestionMark,
    title: 'FAQs',
    description: 'Advice and answers from the MyCanvas team, organized by product.',
    linkLabel: 'Open the help center',
    url: SETTINGS.HELPDESK_URL,
    external: true,
  },
  {
    id: 'video',
    Icon: CirclePlay,
    title: 'Video tutorials',
    description: 'Learn how to create and edit MyCanvas projects, step by step.',
    linkLabel: 'Watch on YouTube',
    url: SETTINGS.YOUTUBE_URL,
    external: true,
  },
  {
    id: 'shipping',
    Icon: Truck,
    title: 'Shipping and returns',
    description: 'See our shipping options, production times and rates for your location.',
    linkLabel: 'See shipping details',
    url: '/shipping',
    external: false,
  },
  {
    id: 'status',
    Icon: Activity,
    title: 'Site status',
    description: 'Something not loading? Check whether MyCanvas is running normally.',
    linkLabel: 'Check site status',
    url: SETTINGS.STATUS_URL,
    external: true,
  },
];

const HelpPathTile = ({ Icon, title, description, linkLabel, url, external }) => {
  return (
    <li className={styles.tile}>
      <span className={styles.iconBadge} aria-hidden="true">
        <Icon size={28} />
      </span>
      <div className={styles.tileText}>
        <h3 className={styles.tileTitle}>{title}</h3>
        <p className={styles.tileDescription}>{description}</p>
      </div>
      {external ? (
        <a href={url} className={styles.tileLink} target="_blank" rel="noopener noreferrer">
          {linkLabel}
          <ExternalLink size={18} aria-hidden="true" />
          <span className={styles.visuallyHidden}> (opens in a new tab)</span>
        </a>
      ) : (
        <Link href={url} className={styles.tileLink}>
          {linkLabel}
          <ArrowRight size={18} aria-hidden="true" />
        </Link>
      )}
    </li>
  );
};

const SupportPage = () => (
  <>
    <Head
      title="Help & Support"
      description="Get help with your MyCanvas orders and print projects. Browse popular support topics and resources, or reach out to our team for more help."
    />

    <Layout>
      <div className={styles.page}>
        <section className={styles.hero}>
          <div className={`${styles.container} ${styles.heroRow}`}>
            <div className={styles.intro}>
              <p className={styles.eyebrow}>Help &amp; support</p>
              <h1 className={styles.title}>Support</h1>
              <p className={styles.lead}>
                MyCanvas support is here to help with your family history books, posters and
                calendars. Find an answer in our resources below, or write to our team.
              </p>
            </div>

            <div className={styles.contactPanel}>
              <h2 className={styles.panelTitle}>
                <Mail size={28} aria-hidden="true" />
                Email our team
              </h2>
              <p className={styles.panelBody}>
                Ask us anything about an order, a project or the editor, or share your feedback. If
                you have an order number, add it to your email.
              </p>
              <div className={styles.emailRow}>
                <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.emailAddress}>
                  {SUPPORT_EMAIL}
                </a>
                <CopyEmailButton />
              </div>
            </div>
          </div>
        </section>

        <section className={styles.helpPaths} aria-labelledby="support-help-paths">
          <div className={styles.container}>
            <h2 id="support-help-paths" className={styles.sectionTitle}>
              Find an answer yourself
            </h2>
            <ul className={styles.tiles}>
              {HELP_PATHS.map(({ id, ...path }) => (
                <HelpPathTile key={id} {...path} />
              ))}
            </ul>
          </div>
        </section>
      </div>
    </Layout>
  </>
);

export default SupportPage;
